// Breadcrumb label registry. A page can name a URL segment it owns (e.g. a widget
// ID → the widget's name) so the top-bar breadcrumb shows words, not UUIDs.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface Registry {
  labels: Record<string, string>;
  register: (segment: string, label: string) => void;
}

const BreadcrumbContext = createContext<Registry>({ labels: {}, register: () => {} });

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<Record<string, string>>({});
  const register = useCallback((segment: string, label: string) => {
    setLabels((prev) => (prev[segment] === label ? prev : { ...prev, [segment]: label }));
  }, []);
  const value = useMemo(() => ({ labels, register }), [labels, register]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/** Page-registered labels, keyed by URL segment. */
export function useBreadcrumbLabels(): Record<string, string> {
  return useContext(BreadcrumbContext).labels;
}

/** Name a URL segment for the breadcrumb while this component is mounted. */
export function useBreadcrumbLabel(segment: string | undefined, label: string | undefined) {
  const { register } = useContext(BreadcrumbContext);
  useEffect(() => {
    if (segment && label) register(segment, label);
  }, [segment, label, register]);
}
